import { useState } from "react"
import { Redirect } from "react-router-dom"
import styled from "styled-components"

import {
  Banner,
  Checkbox,
  Select,
  spacing,
  Text,
  TextArea,
} from "@minuthq/meatball-ui-react"

import { usePostTrackingBetaEnrolledEvent } from "src/data/analytics/queries/taskManagementAnalyticsQueries"
import { useBackendFlags } from "src/data/flags/useBackendFlags"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { usePutEnrollBeta } from "src/data/taskManagement/taskManagementQueries"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { MButton } from "src/ui/Button/MButton"
import ApartHotelBedLandscape from "src/ui/images/ai-gen-aparthotel-landscape.jpg"
import ApartHotelBedPortrait from "src/ui/images/ai-gen-aparthotel-portrait.jpg"
import { BackdropView } from "src/ui/Layout/BackdropView"
import { InternalLink } from "src/ui/Link/InternalLink"

export function TaskManagementBetaSignup() {
  const { t, langKeys } = useTranslate()
  const { orgId } = useOrganization()
  const { task_management_released } = useBackendFlags()

  const [challenges, setChallenges] = useState("")
  const [benefits, setBenefits] = useState("")
  const [teamSize, setTeamSize] = useState("")
  const [checkedBetaSignupError, setCheckedBetaSignupError] = useState(false)

  const employeesString = t(langKeys.task_management_beta_application_employees)

  const teamSizeOptions = [
    {
      label: "",
      value: "",
      hidden: true,
    },
    {
      label: "1-5 " + employeesString,
      value: "1-5",
    },
    {
      label: "5-50 " + employeesString,
      value: "5-50",
    },
    {
      label: "50-100 " + employeesString,
      value: "50-100",
    },
    {
      label: "100+ " + employeesString,
      value: "100+",
    },
  ]

  const [isChecked, setIsChecked] = useState(false)

  const putEnroll = usePutEnrollBeta()
  const postTrackingBetaEnrolledEvent = usePostTrackingBetaEnrolledEvent()

  function putEnrolled() {
    putEnroll.mutate({
      path: {
        organization_id: orgId,
      },
    })
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    if (!isChecked) {
      setCheckedBetaSignupError(true)
      return
    }

    postTrackingBetaEnrolledEvent.mutate(
      {
        challenges,
        benefits,
        teamSize,
        copy: "A",
      },
      {
        onSuccess: () => {
          putEnrolled()
        },
      }
    )
  }

  const isError = putEnroll.isError || postTrackingBetaEnrolledEvent.isError

  if (task_management_released) {
    return <Redirect to={Routes.TaskManagement.location().pathname} />
  }

  return (
    <BackdropView
      landscapeImageUrl={ApartHotelBedLandscape}
      portraitImageUrl={ApartHotelBedPortrait}
      title="Tasks"
    >
      <form onSubmit={handleSubmit}>
        <Content>
          <Section>
            <Text variant="heading2">
              {t(langKeys.task_management_beta_application_header)}
            </Text>
            <Text variant="body">
              {t(langKeys.task_management_beta_application_description)}
            </Text>
          </Section>
          <Section>
            <Text variant="subtitle">
              {t(langKeys.task_management_beta_application_question1)}
            </Text>
            <TextArea
              onChange={setChallenges}
              value={challenges}
              maxLength={500}
              minRows={4}
              required
              placeholder={t(
                langKeys.task_management_beta_application_placeholder1
              )}
            />
          </Section>
          <Section>
            <Text variant="subtitle">
              {t(langKeys.task_management_beta_application_question2)}
            </Text>
            <TextArea
              onChange={setBenefits}
              value={benefits}
              maxLength={500}
              minRows={4}
              required
              placeholder={t(
                langKeys.task_management_beta_application_placeholder2
              )}
            />
          </Section>
          <Section>
            <Text variant="subtitle">
              {t(langKeys.task_management_beta_application_question3)}
            </Text>
            <Select
              value={teamSize}
              onChange={(selectedValue) => setTeamSize(selectedValue)}
              options={teamSizeOptions}
              required
              label={t(langKeys.task_management_beta_application_placeholder3)}
            />
          </Section>
          <Section>
            <Banner type="info">
              <Checkbox
                label={t(langKeys.task_management_beta_application_question4)}
                checked={isChecked}
                onCheck={setIsChecked}
                error={checkedBetaSignupError}
              />
            </Banner>
            {checkedBetaSignupError && !isChecked && (
              <Banner type="error" fullWidth>
                {t(langKeys.task_management_beta_signup_error)}
              </Banner>
            )}
          </Section>

          {isError && (
            <Section>
              <Banner type="error">
                {t(langKeys.failed_general_error_try_refreshing_page)}
              </Banner>
            </Section>
          )}
        </Content>

        <ButtonContainer>
          <InternalLink to={Routes.TaskManagementBetaStart.location()}>
            {t(langKeys.close)}
          </InternalLink>
          <MButton type="submit">
            {t(langKeys.task_management_beta_application_cta)}
          </MButton>
        </ButtonContainer>
      </form>
    </BackdropView>
  )
}

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${spacing.XL};
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.L};
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.M};
`
